import { useMemo } from 'react';
import tw from 'twin.macro';

import { EpisodesTable } from '@insights_module/components/show_details_screen/EpisodesTable';
import { Layout } from '@insights_module/components/Layout';
import { ShowDetailsSideBar } from '@insights_module/components/show_details_screen/ShowDetailsSidebar';
import { CloseIcon } from '@icons/CloseIcon';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { Spacer } from '@components/Spacer';
import { CaretIcon } from '@icons/CaretIcon';
import { validateArtworkUrl } from '@insights_module/utils/showsUtils';
import { formatNumberWithCommas } from '@utils/numbers';
import { useShowDetailsContext } from '@insights_module/context/ShowDetailsContext';
import { TableScrollTip } from '@insights_module/components/TableScrollTip';
import { useSelectedShowDetails } from '@insights_module/hooks/useGraphqlShowDetails';
import { useLibraryTableData } from '@insights_module/hooks/useLibraryTableData';
import { SidebarToggleButton } from '@insights_module/components/SidebarToggleButton';

export const DRAWER_HEADER_HEIGHT = '66';

const Background = tw.div`absolute top-[1px] bottom-0 right-0 left-0 bg-page-background z-30 px-3 pt-[11px] pb-3 transition-all ease-in-out duration-300`;
const InnerWrapper = tw.div`relative flex flex-row h-full`;
const Header = tw.div`flex w-full justify-between h-[${DRAWER_HEADER_HEIGHT}px] p-2.5 select-none bg-neutral-background rounded-t-md border-b border-gray-3`;
const CloseButton = tw.div`w-[34px] h-[34px] flex items-center justify-center cursor-pointer border border-gray-1 rounded hover:bg-gray-2`;
const ShowTitle = tw.div`flex items-center text-text-and-icon text-[13px] pl-2 font-semibold select-text`;
const CoverImage = tw.img`w-6 h-6 rounded-[2px] border-[0.5px] border-gray-1`;
const LeftWrapper = tw.div`flex items-center`;
const RightWrapper = tw.div`flex justify-center items-center gap-1`;
const Button = tw.button`flex items-center justify-center w-[34px] h-[34px] rounded border border-gray-3 bg-neutral-background cursor-pointer hover:bg-gray-2`;
const ButtonIcon = tw(CaretIcon)`fill-text-and-icon w-[10px] h-[5px]`;
const ShowsCount = tw.p`block text-text-and-icon text-[13px]`;

const disabledButtonStyles = tw`text-text-and-icon-muted cursor-not-allowed opacity-60`;

const ShowDetailsDrawer = () => {
  const {
    setSearchTerm,
    setSelectedShowDetailsGarmCategory,
    setSelectedShowDetailsGarmRisk,
    setSelectedShowDetailNamedEntity,
    setSelectedShowDetailsCategory,
  } = useShowDetailsContext();

  const { selectedShow, setSelectedShow, setEpisodeListExpanded } = useInsightsContext();

  const { getNextShow, getPrevShow, getShowIndex, totalGroupsCount, groupsCurrentPage } =
    useLibraryTableData();

  const currentShowIndex = useMemo(() => {
    const index = getShowIndex(selectedShow);
    return (groupsCurrentPage - 1) * 20 + index;
  }, [selectedShow]);

  const { data: show } = useSelectedShowDetails();

  const handleCloseDrawer = () => {
    setSelectedShow('');
    setSearchTerm('');
    setEpisodeListExpanded(false);
    setSelectedShowDetailsGarmCategory(null);
    setSelectedShowDetailsGarmRisk(null);
    setSelectedShowDetailNamedEntity(null);
    setSelectedShowDetailsCategory(null);
  };

  const handleNextShow = () => {
    const nextShow = getNextShow(selectedShow);
    if (nextShow?.id) {
      setSelectedShow(nextShow.id);
    }
  };

  const handlePreviousShow = () => {
    const previousShow = getPrevShow(selectedShow);
    if (previousShow?.id) {
      setSelectedShow(previousShow.id);
    }
  };

  if (!selectedShow) {
    return null;
  }

  return (
    <Background data-testid='show-details-drawer'>
      <Layout.Card tw='shadow-primary bg-neutral-background'>
        <Header>
          <LeftWrapper>
            <SidebarToggleButton tw='mr-[8px]' mode='close' />
            <Spacer direction='horizontal' size='xs' />
            <CloseButton onClick={handleCloseDrawer}>
              <CloseIcon tw='fill-text-and-icon' />
            </CloseButton>
            <Spacer direction='horizontal' size='xs' />
            <CoverImage
              tw='w-[48px] h-[48px]'
              src={validateArtworkUrl(show?.thumbnail_url)}
              alt={show?.name}
            />
            <ShowTitle>{show?.name}</ShowTitle>
          </LeftWrapper>
          <RightWrapper>
            <ShowsCount>{`${formatNumberWithCommas(
              currentShowIndex + 1
            )} / ${formatNumberWithCommas(totalGroupsCount)}`}</ShowsCount>
            <Button
              onClick={handlePreviousShow}
              css={[!currentShowIndex && disabledButtonStyles]}
              disabled={!currentShowIndex}>
              <ButtonIcon className='rotate-180' />
            </Button>
            <Button
              onClick={handleNextShow}
              css={[currentShowIndex === totalGroupsCount - 1 && disabledButtonStyles]}
              disabled={currentShowIndex === totalGroupsCount - 1}>
              <ButtonIcon />
            </Button>
          </RightWrapper>
        </Header>

        <InnerWrapper tw='overflow-hidden'>
          <EpisodesTable />
          <ShowDetailsSideBar />
          <TableScrollTip />
        </InnerWrapper>
      </Layout.Card>
    </Background>
  );
};

export { ShowDetailsDrawer };
